import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Home.css";

import pendantImage from "../assets_optimized/pendant.webp";
import chairImage from "../assets_optimized/chair.webp";
import plantImage from "../assets_optimized/plant.webp";
import wallpaperImage from "../assets_optimized/wallpaper.webp";

import livingImg from "../assets_optimized/4.webp";
import sideboardImg from "../assets_optimized/img2-hero.webp";
import bedroomImg from "../assets_optimized/img3-hero.webp";
import paperBg from "../assets_optimized/back.webp";

import Icon from "./Icon.jsx";
import { useLamp } from "../contexts/LampContext.jsx";
import SEO from "../components/SEO.jsx";

import FeatureGrid from "./FeatureGrid.jsx";
import About from "./About.jsx";

/** ✅ Fix A: preload an image and tell us when it’s ready */
function useImageLoaded(src) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!src) return;

    let cancelled = false;
    const img = new Image();
    img.decoding = "async";

    img.onload = () => {
      if (!cancelled) setLoaded(true);
    };
    img.onerror = () => {
      // fail open so UI doesn't get stuck
      if (!cancelled) setLoaded(true);
    };

    img.src = src;

    return () => {
      cancelled = true;
    };
  }, [src]);

  return loaded;
}

export default function Home() {
  const { isLampOn, setIsLampOn } = useLamp();

  const heroRef = useRef(null);

  const pendantRef = useRef(null);
  const chairRef = useRef(null);
  const plantRef = useRef(null);
  const headingRef = useRef(null);
  const offTextRef = useRef(null);

  const rafRef = useRef(null);
  const targetRef = useRef({ x: 0, y: 0 });
  const smoothRef = useRef({ x: 0, y: 0 });
  const insideRef = useRef(false);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  /** ✅ preload paper background for stats section */
  const paperLoaded = useImageLoaded(paperBg);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const heroEl = heroRef.current;
    if (!heroEl) return;

    const pendantEl = pendantRef.current;
    const chairEl = chairRef.current;
    const plantEl = plantRef.current;
    const headingEl = headingRef.current;
    const offTextEl = offTextRef.current;

    const setXY = (el, x, y) => {
      if (!el) return;
      el.style.setProperty("--px", `${x.toFixed(2)}px`);
      el.style.setProperty("--py", `${y.toFixed(2)}px`);
    };

    const apply = (nx, ny) => {
      const w = window.innerWidth || 1200;
      const scale = w < 520 ? 0.55 : w < 900 ? 0.8 : 1;

      setXY(pendantEl, nx * 8 * scale, ny * 6 * scale);
      setXY(chairEl, nx * 18 * scale, ny * 14 * scale);
      setXY(plantEl, nx * 14 * scale, ny * 18 * scale);
      setXY(headingEl, nx * -10 * scale, ny * -6 * scale);
      setXY(offTextEl, nx * -7 * scale, ny * -4 * scale);
    };

    const tick = () => {
      const t = targetRef.current;
      const s = smoothRef.current;

      const ease = 0.085;
      s.x += (t.x - s.x) * ease;
      s.y += (t.y - s.y) * ease;

      apply(s.x, s.y);
      rafRef.current = requestAnimationFrame(tick);
    };

    const onEnter = () => (insideRef.current = true);
    const onLeave = () => {
      insideRef.current = false;
      targetRef.current = { x: 0, y: 0 };
    };

    const onMouseMove = (e) => {
      if (!insideRef.current) return;

      const rect = heroEl.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;

      const dx = (e.clientX - cx) / (rect.width / 2);
      const dy = (e.clientY - cy) / (rect.height / 2);

      targetRef.current.x = Math.max(-1, Math.min(1, dx));
      targetRef.current.y = Math.max(-1, Math.min(1, dy));
    };

    const onScroll = () => {
      const rect = heroEl.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      if (rect.bottom < 0 || rect.top > vh) return;

      const progress = Math.max(-1, Math.min(1, (rect.top / vh) * -1));
      targetRef.current.y = Math.max(-1, Math.min(1, progress * 0.55));
    };

    const onDeviceOrientation = (event) => {
      const g = event.gamma ?? 0;
      const b = event.beta ?? 0;
      targetRef.current.x = Math.max(-1, Math.min(1, g / 25));
      targetRef.current.y = Math.max(-1, Math.min(1, b / 35));
    };

    targetRef.current = { x: 0, y: 0 };
    smoothRef.current = { x: 0, y: 0 };
    apply(0, 0);

    rafRef.current = requestAnimationFrame(tick);

    heroEl.addEventListener("mouseenter", onEnter, { passive: true });
    heroEl.addEventListener("mouseleave", onLeave, { passive: true });
    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("deviceorientation", onDeviceOrientation, true);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      heroEl.removeEventListener("mouseenter", onEnter);
      heroEl.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("deviceorientation", onDeviceOrientation, true);
    };
  }, [prefersReducedMotion]);

  return (
    <div className="homePage">
      <SEO
        title="Protek Studio Design - Interior & Design Studio | Elegant Spaces for Every Scale"
        description="Protek Studio Design - Design studio specializing in elegant interiors and luxury spaces for projects of all scales—from boutique spaces to large ventures and commercial buildings. Creating sophisticated environments that captivate and inspire."
        keywords="interior design, luxury interiors, hotel design, commercial buildings, large scale projects, ventures, residential design, commercial interiors, elegant design, sophisticated spaces"
        url="/"
        image="/logo512.png"
      />
      <div
        ref={heroRef}
        className={`heroSection ${isLampOn ? "lampOn" : "lampOff"}`}
        style={
          isLampOn
            ? {
              backgroundImage: `url(${wallpaperImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "scroll",
            }
            : {}
        }
      >
        <div style={{ display: "none" }}>
          <img src={wallpaperImage} alt="" fetchPriority="high" />
          <img src={paperBg} alt="" fetchPriority="low" />
        </div>

        {/* <Link to="/" className="heroLogo">
          <div className="logoWrapper">
            <div className="logoText">PS</div>
          </div>
        </Link> */}

        <div
          ref={pendantRef}
          className="heroPendantWrapper parallaxLayer parallaxPendant"
          aria-hidden={!isLampOn}
        >
          <div className="heroPendantGlow"></div>
          <img
            src={pendantImage}
            alt="Pendant lamp"
            className="heroPendant"
            width="220"
            height="300"
            fetchPriority="high"
          />
        </div>

        {!isLampOn && (
          <>
            <div className="stringLights">
              {Array.from({ length: 15 }).map((_, i) => (
                <div key={i} className="lightBulb"></div>
              ))}
            </div>

            <div
              ref={offTextRef}
              className="offStateText parallaxLayer parallaxTextOff"
            >
              <h1 className="offStateLine1">PROTEK</h1>
              <h2 className="offStateLine2">DESIGN STUDIO</h2>
            </div>
          </>
        )}

        {isLampOn && (
          <>
            <div
              ref={headingRef}
              className="heroHeading parallaxLayer parallaxTextOn"
            >
              <h1 className="heroLine1">PROTEK</h1>
              <h2 className="heroLine2">DESIGN STUDIO</h2>
              <Link to="/services" className="heroCtaBtn">
                Explore Our Services <Icon name="arrow" />
              </Link>
            </div>

            <div className="heroVisuals">
              <img
                ref={chairRef}
                src={chairImage}
                alt="Armchair"
                className="heroChair parallaxLayer parallaxChair"
                decoding="async"
                loading="eager"
                fetchPriority="high"
                width="600"
                height="800"
              />
              <img
                ref={plantRef}
                src={plantImage}
                alt="Snake plant"
                className="heroPlant parallaxLayer parallaxPlant"
                decoding="async"
                loading="eager"
                fetchPriority="high"
                width="320"
                height="500"
              />
            </div>
          </>
        )}

        <div className="toggleWrapper">
          <span className="toggleLabel toggleLabelTop">Off</span>
          <button
            className="toggleSwitch"
            onClick={() => setIsLampOn(!isLampOn)}
            aria-label={isLampOn ? "Turn off" : "Turn on"}
          >
            <div className={`toggleKnob ${isLampOn ? "toggleOn" : ""}`}></div>
          </button>
          <span className="toggleLabel toggleLabelBottom">On</span>
        </div>
      </div>

      {isLampOn && (
        <section
          className={`homeStatsSection ${paperLoaded ? "bgReady" : "bgLoading"}`}
          style={
            paperLoaded
              ? {
                backgroundImage: `url(${paperBg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "repeat",
              }
              : {}
          }
        >
          <div className="statsSectionContainer">
            <div className="statsKeywordsRow">
              <span className="keyword">LUXURY</span>
              <span className="keyword">CRAFTSMANSHIP</span>
              <span className="keyword">ELEGANCE</span>
              <span className="keyword">OPULENCE</span>
              <span className="keyword">MAJESTIC</span>
            </div>

            <div className="statsDivider"></div>

            <div className="statsContentGrid">
              <div className="statsImageCollage">
                <div className="collageItem collageLeft">
                  <img
                    src={livingImg}
                    alt="Living room"
                    className="collageImage"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                <div className="collageRightStack">
                  <div className="collageItem collageRightTop">
                    <img
                      src={sideboardImg}
                      alt="Sideboard"
                      className="collageImage"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  <div className="collageItem collageRightBottom">
                    <img
                      src={bedroomImg}
                      alt="Bedroom"
                      className="collageImage"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </div>

              <div className="statsBadges">
                <div className="sealBadge">
                  <div className="sealBig">20+</div>
                  <div className="sealSmall">Satisfied Clients</div>
                </div>
                <div className="sealBadge">
                  <div className="sealBig">20+</div>
                  <div className="sealSmall">Unique Styles</div>
                </div>
                <div className="sealBadge">
                  <div className="sealBig">20+</div>
                  <div className="sealSmall">Project Complete</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {isLampOn && (
        <div className="homeFeaturesContent">
          <FeatureGrid />
          <About />
        </div>
      )}
    </div>
  );
}
